<script lang="ts" setup>
  import { computed } from "vue";
  import { useAppStore } from "@/stores/app";
  import { useSessionStore } from "@/stores/session";
  import { useRoute, useRouter } from "vue-router";
  import SearchField from "@/components/views/rewards/SearchField.vue";
  import NavTab from "@/components/navigation/NavTab.vue";
  import NavLogo from "@/components/navigation/NavLogo.vue";
  import MobileNavTab from "@/components/navigation/MobileNavTab.vue";
  import NavDropDown from "@/components/navigation/NavDropDown.vue";
  import * as amplitude from "@amplitude/analytics-browser";
  import { useI18n } from "vue-i18n";
  import { UserPermissionFlag } from "@/models/user";
  import { useCardsStore } from "@/stores/cards";
  import { MembershipStatus } from "@/models/membership";

  const currentRoute = useRoute();
  const appStore = useAppStore();
  const sessionStore = useSessionStore();
  const cardsStore = useCardsStore();
  const router = useRouter();

  const canNavigate = computed(() =>
    sessionStore.membership?.status === MembershipStatus.ACTIVE
    || sessionStore.membership?.status === MembershipStatus.OVERDUE
    || sessionStore.loggedUser?.permissionFlag === UserPermissionFlag.ADMIN,
  );

  const offersHidden = computed(() => {
    return appStore.isAmexUxLimited && cardsStore.hasOnlyAmexCards;
  });

  const showSearchInput = computed(() => {
    return !(appStore.isNewOffersSystem || offersHidden.value);
  });

  const { t } = useI18n();

  function isRouteActive(route: string): string {
    return currentRoute.path === route ? 'active' : '';
  }

  function handleReferralClick() {
    if (appStore.featureFlags.newReferralSystem) {
      router.push({ name: 'referral' });
    } else {
      appStore.showReferralRockModal = true;
    }

    amplitude.track('Invite Friends Clicked', {
      'In-App Location': 'Navigation bar',
    });
  }
</script>

<template>
  <v-container tag="header" class="navbar">
    <div :class="['nav-grid', {'with-search': showSearchInput}]">
      <nav-logo class="logo"/>
      <template v-if="cardsStore.initialized">
        <div class="links">
          <nav-tab
            v-if="!offersHidden"
            to="/rewards"
            prepend-icon="local_atm"
            :class="isRouteActive('/rewards')"
            :disabled="!canNavigate"
          >
            {{ $t('nav.all-deals') }}
          </nav-tab>
          <nav-tab
            v-if="appStore.isReferralEnabled"
            to=""
            prepend-icon="redeem"
            @click="handleReferralClick"
            :disabled="!canNavigate"
          >
            {{ $t('nav.refer') }}
          </nav-tab>
          <nav-tab
            v-if="sessionStore.canSeeVideosSection"
            to="/videos" prepend-icon="subscriptions"
            :class="isRouteActive('/videos')"
            :disabled="!canNavigate"
          >
            {{ $t('nav.videos') }}
          </nav-tab>
          <nav-tab
            v-if="!offersHidden"
            to="/activity" prepend-icon="credit_card"
            :class="isRouteActive('/activity')"
            :disabled="!canNavigate"
          >
            {{ $t('nav.my-card') }}
          </nav-tab>
          <nav-drop-down prepend-icon="account_circle" :label="$t('nav.my-account')">
            <v-list width="150" style="transform: translateY(10px)">
              <v-list-item>
                <router-link to="/account" class="d-block pt-2 pb-2">
                  {{ $t('nav.my-profile') }}
                </router-link>
                <router-link :to="currentRoute.path" class="d-block pt-2 pb-2" @click="sessionStore.logout()">
                  {{ $t('nav.logout') }}
                </router-link>
              </v-list-item>
            </v-list>
          </nav-drop-down>
        </div>
        <div class="search" v-if="showSearchInput">
          <SearchField :label="$t('nav.search')"/>
        </div>
      </template>
    </div>
  </v-container>

  <!-- Mobile Navbar -->
  <v-bottom-navigation class="navbar-mobile">
    <mobile-nav-tab
      v-if="!offersHidden"
      icon="home"
      to="/rewards"
      :label="$t('nav.all-deals')"
      :disabled="!canNavigate"
    />
    <mobile-nav-tab
      v-if="appStore.isReferralEnabled"
      icon="redeem"
      to=""
      :label="$t('nav.refer')"
      @click="handleReferralClick"
      :disabled="!canNavigate"
    />
    <mobile-nav-tab
      v-if="sessionStore.canSeeVideosSection"
      icon="subscriptions" to="/videos" :label="$t('nav.videos')"
      :disabled="!canNavigate"
    />
    <mobile-nav-tab
      v-if="!offersHidden"
      icon="credit_card"
      to="/activity"
      :label="$t('nav.my-card')"
      :disabled="!canNavigate"
    />
    <mobile-nav-tab
      icon="account_circle"
      to="/account"
      :label="$t('nav.my-account')"
    />
  </v-bottom-navigation>

</template>

<style lang="scss" scoped>
  .navbar {
    .nav-grid {
      height: 120px;
      width: 100%;
      display: grid;
      grid-template-columns: 1fr minmax(0, 3fr);
      grid-template-areas: "logo navigation";
      grid-gap: 2rem;

      .logo {
        grid-area: logo;
      }

      .links {
        grid-area: navigation;

        height: 100%;
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
        align-items: center;
        padding: 0;
        gap: 4rem;
        white-space: nowrap;
      }

      .search {
        grid-area: search;
        align-self: center;
      }

      &.with-search {
        grid-template-columns: 1fr minmax(0, 3fr) 1fr;
        grid-template-areas: "logo navigation search";

        .links {
          justify-content: center;
        }
      }
    }

  }

  .navbar-mobile {
    display: none;
  }

  @media screen and (max-width: breakpoint(lg) - 1px) {
    .navbar {
      .nav-grid.with-search {
        grid-template-columns: 1fr minmax(0, 3fr) auto;
      }
    }
  }

  @media screen and (max-width: breakpoint(md) - 1px) {
    .navbar {
      .nav-grid {
        .links {
          display: none;
        }
      }
    }

    .navbar-mobile {
      display: block;
      position: fixed !important;

      :deep(.v-bottom-navigation__content) {
        height: 100%;
        align-items: center;
        justify-content: space-around;
      }

      :deep(.v-btn) {
        padding-left: 2px;
        padding-right: 2px;
      }

      :deep(.v-btn--active) {
        .v-btn__overlay {
          background-color: transparent;
        }
      }
    }
  }
</style>